import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { ArrowLeft, Award, Globe, Factory, Target, Leaf, Shield, Zap, Handshake } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';

const AboutPage = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'About Us',
      subtitle: 'Over 40 years of excellence in labeling and packaging solutions',
      story: {
        title: 'Our Story',
        paragraphs: [
          'Since 1983, Etiteks has been a label and packaging manufacturer based in Istanbul. What started as a small workshop has grown into one of Turkey\'s most comprehensive label manufacturers, serving fashion, sportswear, and beauty brands across more than 30 countries.',
          'We operate two product lines: Etiteks for woven labels, care labels, hang tags and sustainable labels, and Pack by Etiteks for cosmetic folding boxes and packaging.'
        ]
      },
      mission: {
        title: 'Our Mission',
        text: 'To deliver tailor-made label and packaging solutions with consistent quality, sustainable materials, and flexible service, building long-term partnerships with fashion brands and textile manufacturers.'
      },
      vision: {
        title: 'Our Vision',
        text: 'To be one of Europe\'s most trusted label and packaging partners, recognized for sustainability, reliability, and flexibility.'
      },
      values: {
        title: 'Our Values',
        items: [
          {
            icon: 'quality',
            title: 'Quality Excellence',
            description: 'We maintain the highest standards in every product we create, backed by OEKO-TEX, FSC, and GRS certifications.'
          },
          {
            icon: 'sustainability',
            title: 'Sustainability',
            description: 'Committed to eco-friendly practices and certified materials that protect our planet for future generations.'
          },
          {
            icon: 'speed',
            title: 'Speed & Reliability',
            description: 'Fast turnaround times with 2-3 day delivery to Europe, flexible quantities and dependable production schedules.'
          },
          {
            icon: 'partnership',
            title: 'European Partnership',
            description: 'Building lasting partnerships with European brands through tailored solutions, zero EU import duty, and dedicated support.'
          }
        ]
      },
      stats: [
        { number: '43+', label: 'Years of Experience' },
        { number: '30+', label: 'Countries Served' },
        { number: '500+', label: 'Active Clients' }
      ],
      certifications: {
        title: 'Our Certifications',
        items: [
          { name: 'OEKO-TEX® Standard 100', description: 'Tested for harmful substances.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/z6os3fp8_sdadadsdadas.png' },
          { name: 'OEKO-TEX® Recycled', description: 'Certified for recycled textile products.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/b1kii4km_OEKO-TEX%C2%AE%20Recycled%20%E2%80%94%20Certified%20for%20recycled%20textile%20products.png' },
          { name: 'FSC®', description: 'Responsibly sourced paper and board.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/uhrbtqt7_FSC%C2%AE%20%E2%80%94%20Responsibly%20sourced%20paper%20and%20board.png' },
          { name: 'GRS', description: 'Recycled content with social and environmental criteria.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rd43kxco_Global%20Recycled%20Standard%20%28GRS%29%20%E2%80%94%20Recycled%20content%20with%20social%20and%20environmental%20criteria.png' },
          { name: 'RCS', description: 'Recycled content traceability.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/qz6kus10_Recycled%20Claim%20Standard%20%28RCS%29%20%E2%80%94%20Recycled%20content%20traceability.png' },
          { name: 'PETA Approved Vegan', description: 'No animal-derived materials.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rt486shs_PETA%20Approved%20Vegan%20%E2%80%94%20No%20animal-derived%20materials.png' },
          { name: 'Sedex', description: 'Audit-ready social compliance.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/91jzm374_SEDEX%20-%20SMETA%20%E2%80%94%20Audit-ready%20social%20compliance.png' }
        ]
      },
      cta: {
        title: 'Ready to Partner With Us?',
        description: 'Let\'s discuss how we can help elevate your brand with our premium labeling solutions.',
        button: 'Contact Us'
      }
    },
    tr: {
      title: 'Hakkımızda',
      subtitle: 'Etiketleme ve ambalaj çözümlerinde 40 yılı aşkın mükemmellik',
      story: {
        title: 'Hikayemiz',
        paragraphs: [
          '1983 yılından bu yana Etiteks, İstanbul merkezli bir etiket ve ambalaj üreticisidir. Küçük bir atölye olarak başlayan yolculuğumuz, bugün Türkiye\'nin en kapsamlı etiket üreticilerinden biri haline gelmiş; 30\'dan fazla ülkedeki moda, spor giyim ve güzellik markalarına hizmet vermektedir.',
          'İki ürün hattımız bulunmaktadır: Dokuma etiketler, bakım etiketleri, karton etiketler ve sürdürülebilir etiketler için Etiteks — kozmetik katlanır kutular ve ambalaj için Pack by Etiteks.'
        ]
      },
      mission: {
        title: 'Misyonumuz',
        text: 'Tutarlı kalite, sürdürülebilir malzemeler ve esnek hizmetle moda markaları ve tekstil üreticileriyle uzun vadeli ortaklıklar kurarak, özel tasarım etiket ve ambalaj çözümleri sunmak.'
      },
      vision: {
        title: 'Vizyonumuz',
        text: 'Sürdürülebilirlik, güvenilirlik ve esneklik ile tanınan, Avrupa\'nın en güvenilir etiket ve ambalaj ortaklarından biri olmak.'
      },
      values: {
        title: 'Değerlerimiz',
        items: [
          {
            icon: 'quality',
            title: 'Kalite Mükemmelliği',
            description: 'OEKO-TEX, FSC ve GRS sertifikalarıyla desteklenen, ürettiğimiz her üründe en yüksek standartları koruyoruz.'
          },
          {
            icon: 'sustainability',
            title: 'Sürdürülebilirlik',
            description: 'Gezegenimizi gelecek nesiller için koruyan çevre dostu uygulamalara ve sertifikalı malzemelere bağlıyız.'
          },
          {
            icon: 'speed',
            title: 'Hız & Güvenilirlik',
            description: 'Avrupa\'ya 2-3 günde teslimat, esnek miktarlar ve güvenilir üretim takvimi ile hızlı geri dönüş süreleri.'
          },
          {
            icon: 'partnership',
            title: 'Avrupa Ortaklığı',
            description: 'Özel çözümler, sıfır AB ithalat vergisi ve özel destek ile Avrupalı markalarla kalıcı ortaklıklar kuruyoruz.'
          }
        ]
      },
      stats: [
        { number: '43+', label: 'Yıllık Deneyim' },
        { number: '30+', label: 'Ülkeye İhracat' },
        { number: '500+', label: 'Aktif Müşteri' }
      ],
      certifications: {
        title: 'Sertifikalarımız',
        items: [
          { name: 'OEKO-TEX® Standard 100', description: 'Zararlı maddeler için test edilmiştir.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/j1dw19xq_02-02-oeko-tex-logo-100.jpg' },
          { name: 'OEKO-TEX® Recycled', description: 'Geri donusturulmus tekstil urunleri icin sertifikali.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/b1kii4km_OEKO-TEX%C2%AE%20Recycled%20%E2%80%94%20Certified%20for%20recycled%20textile%20products.png' },
          { name: 'FSC®', description: 'Sorumlu kaynaklardan elde edilen kagit ve karton.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/uhrbtqt7_FSC%C2%AE%20%E2%80%94%20Responsibly%20sourced%20paper%20and%20board.png' },
          { name: 'GRS', description: 'Sosyal ve cevresel kriterlerle geri donusturulmus icerik.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rd43kxco_Global%20Recycled%20Standard%20%28GRS%29%20%E2%80%94%20Recycled%20content%20with%20social%20and%20environmental%20criteria.png' },
          { name: 'RCS', description: 'Geri donusturulmus icerik izlenebilirligi.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/qz6kus10_Recycled%20Claim%20Standard%20%28RCS%29%20%E2%80%94%20Recycled%20content%20traceability.png' },
          { name: 'PETA Approved Vegan', description: 'Hayvansal kaynak icermeyen malzemeler.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rt486shs_PETA%20Approved%20Vegan%20%E2%80%94%20No%20animal-derived%20materials.png' },
          { name: 'Sedex', description: 'Denetim odakli sosyal uyumluluk.', image: 'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/91jzm374_SEDEX%20-%20SMETA%20%E2%80%94%20Audit-ready%20social%20compliance.png' }
        ]
      },
      cta: {
        title: 'Bizimle Çalışmaya Hazır mısınız?',
        description: 'Premium etiketleme çözümlerimizle markanızı nasıl yükseltebileceğimizi konuşalım.',
        button: 'İletişime Geçin'
      }
    }
  };

  const t = content[language];

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'quality': return <Award size={28} />;
      case 'sustainability': return <Leaf size={28} />;
      case 'speed': return <Zap size={28} />;
      case 'partnership': return <Handshake size={28} />;
      default: return <Shield size={28} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[350px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/j2wu35i4_sadasf.png" alt="Woven labels manufactured at the Etiteks facility in Istanbul" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10 pb-12 pt-32">
          <Link 
            to="/" 
            className="inline-flex items-center text-blue-200 hover:text-white transition-colors mb-8 group"
          >
            <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            {language === 'tr' ? 'Ana Sayfa' : 'Home'}
          </Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white shadow-lg relative z-20 -mt-8 mx-6 lg:mx-auto max-w-5xl rounded-2xl">
        <div className="grid grid-cols-3 divide-x divide-gray-100">
          {t.stats.map((stat, index) => (
            <div key={index} className="p-8 text-center">
              <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">{t.story.title}</h2>
              <div className="space-y-6">
                {t.story.paragraphs.map((paragraph, index) => (
                  <p key={index} className="text-lg text-gray-600 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-3xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/m3gznnfr_image.png" 
                  alt="Etiteks Production" 
                  className="w-full h-auto"                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-10 rounded-3xl shadow-lg">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-6">
                <Target size={28} className="text-gray-700" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.mission.title}</h3>
              <p className="text-gray-600 leading-relaxed">{t.mission.text}</p>
            </div>
            
            <div className="bg-gray-900 p-10 rounded-3xl shadow-lg text-white">
              <div className="w-14 h-14 bg-white/15 rounded-xl flex items-center justify-center mb-6">
                <Globe size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">{t.vision.title}</h3>
              <p className="text-gray-300 leading-relaxed">{t.vision.text}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-16">{t.values.title}</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.values.items.map((value, index) => (
              <div key={index} className="text-center group">
              <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-gray-800 group-hover:text-white transition-all text-gray-700">
                  {getIcon(value.icon)}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gray-50" data-testid="certifications-section">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-16 italic">{t.certifications.title}</h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-8 md:gap-10 items-start">
            {t.certifications.items.map((cert, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="h-24 w-24 flex items-center justify-center mb-4">
                  <img src={cert.image} alt={cert.name} className="max-h-24 max-w-24 object-contain" />
                </div>
                <p className="text-sm text-gray-500 italic">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/j2wu35i4_sadasf.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t.cta.title}</h2>
          <p className="text-xl text-gray-200 mb-10">{t.cta.description}</p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
          >
            {t.cta.button}
            <ArrowLeft size={20} className="ml-2 rotate-180" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;